import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from '@mui/styles';
import { theme } from '../../theme/theme';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import {developmentInfo, formatPropertyNumber} from '../../utils/propertyUtils';
import { Property, PropertyInvoiceWithRelations, useGetUserByIdQuery, usePatchPropertyMutation, usePatchPropertyInvoicesByIdMutation } from "../../redux/residentPortalApi";
import LoadingCover from "./LoadingCover";
import developmentsData from "../../resident-portal";
import { useIsHoa } from '../../hooks/useIsHoa';
import ResponsiveRecordCard from './ResponsiveRecordCard';

interface OwnProps {
  property: Property;
  invoices: PropertyInvoiceWithRelations[];
}

const useStyles = makeStyles({
  modalStyle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    maxHeight: '80vh',
    overflowY: 'scroll',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  logoSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  logo: {
    width: '100px',
  },
  invoiceDetails: {
    textAlign: 'right',
  },
  table: {
    marginTop: theme.spacing(3),
  },
  bankDetails: {
    marginTop: theme.spacing(3),
    textAlign: 'left',
  },
  paidStatus: {
    color: 'green',
  },
  overdueStatus: {
    color: 'red',
  },
  dueSoonStatus: {
    color: '#F7C914',
  },
});

const InvoicesList: React.FC<OwnProps> = ({ property, invoices }) => {
  const classes = useStyles();
  const isHoa = useIsHoa();
  const showCards = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);
  const [editWalletOpen, setEditWalletOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number>(property?.walletBalance ?? 0);
  const [editInvoiceOpen, setEditInvoiceOpen] = useState(false);
  const [editInvoiceBalance, setEditInvoiceBalance] = useState<number | null>(null);
  const [editInvoiceStatus, setEditInvoiceStatus] = useState<string>('');
  const [editingInvoice, setEditingInvoice] = useState<PropertyInvoiceWithRelations | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [patchProperty] = usePatchPropertyMutation();
  const [patchPropertyInvoice] = usePatchPropertyInvoicesByIdMutation();

  const handleOpen = (invoice: any) => {
    setSelectedInvoice(invoice);
    setOpen(true);
  };

  const development = developmentsData.developments.find(development => development.name === property?.development);

  const owner1Id = property?.ownerIds?.[0] || null;
  const owner2Id = property?.ownerIds?.[1] || null;


  const { data: owner1 } = useGetUserByIdQuery(
    { id: owner1Id || '' },
    { skip: !owner1Id }
  );

  const { data: owner2 } = useGetUserByIdQuery(
    { id: owner2Id || '' },
    { skip: !owner2Id }
  );


  let owners = [];
  if (owner1) owners.push(owner1);
  if (owner2) owners.push(owner2);

  const handleOpenEditWallet = () => {
    setWalletBalance(property?.walletBalance ?? 0);
    setEditWalletOpen(true);
  };

  const handleCloseEditWallet = () => {
    setEditWalletOpen(false);
  };

  const handleSaveWalletBalance = async () => {
    try {
      await patchProperty({
        propertyPartial: {
          id: property.id,
          walletBalance: walletBalance
        }
      }).unwrap();
      handleCloseEditWallet();
    } catch (error) {
      console.error('Failed to update wallet balance:', error);
    }
  };

  const handleOpenEditInvoice = (invoice: PropertyInvoiceWithRelations) => {
    setEditingInvoice(invoice);
    setEditInvoiceBalance(invoice.propertyInvoiceBalance ?? 0);
    setEditInvoiceStatus(invoice.status ?? 'Unpaid');
    setEditInvoiceOpen(true);
  };

  const handleCloseEditInvoice = () => {
    setEditInvoiceOpen(false);
    setEditingInvoice(null);
  };

  const handleSaveInvoice = async () => {
    if (editingInvoice?.id) {
      try {
        await patchPropertyInvoice({
          id: editingInvoice.id,
          propertyInvoicePartial: {
            propertyInvoiceBalance: editInvoiceBalance ?? 0,
            status: editInvoiceStatus
          }
        }).unwrap();
        handleCloseEditInvoice();
      } catch (error) {
        console.error('Failed to update invoice:', error);
      }
    }
  };

  const handleClose = () => setOpen(false);

  const formatLevyMonth = (dateStr?: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  };

  useEffect(() => {
    setPage(0);
  }, [invoices.length]);

  const paginatedInvoices = useMemo(() => {
    const start = page * rowsPerPage;
    return invoices.slice(start, start + rowsPerPage);
  }, [invoices, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderInvoiceStatus = (status?: string) => (
    status === 'Paid'
      ? 'Paid'
      : status === 'Unpaid'
        ? 'Unpaid'
        : status === 'Cancelled'
          ? 'Cancelled'
          : 'Partially Paid'
  );

  const getInvoiceStatusStyles = (status?: string) => ({
    backgroundColor:
      status === 'Paid'
        ? '#4CAF50'
        : status === 'Unpaid'
          ? '#f44336'
          : status === 'Cancelled'
            ? '#6b7280'
            : '#FFBF00',
    color: '#fff',
  });

  return (
    (invoices && development) ?
      <Box>
        {/* Wallet Balance Section */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Property Wallet Balance</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>${(property?.walletBalance ?? 0).toFixed(2)}</Typography>
            </Box>
            {isHoa && (
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<EditIcon />}
                onClick={handleOpenEditWallet}
              >
                Edit Balance
              </Button>
            )}
          </Box>
        </Paper>

        {showCards ? (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {paginatedInvoices.map((invoice: PropertyInvoiceWithRelations) => {
              const isOverdue = new Date(invoice?.dueDate ?? '') < new Date() && invoice?.status !== 'Paid';

              return (
                <ResponsiveRecordCard
                  key={invoice.invoiceNumber}
                  eyebrow="Levy Invoice"
                  title={invoice.invoiceNumber}
                  badge={<Chip label={renderInvoiceStatus(invoice?.status)} sx={getInvoiceStatusStyles(invoice?.status)} />}
                  fields={[
                    {
                      label: 'Invoice Amount',
                      value: <Typography variant="body1" sx={{ fontWeight: 700 }}>${invoice?.invoice?.totalCost.toFixed(2)}</Typography>,
                    },
                    {
                      label: 'Invoice Balance',
                      value: <Typography variant="body1" sx={{ fontWeight: 700 }}>${(invoice?.propertyInvoiceBalance ?? 0).toFixed(2)}</Typography>,
                    },
                    {
                      label: 'Due Date',
                      value: (
                        <Typography sx={{ color: isOverdue ? 'lightcoral' : '#4CAF50', fontWeight: 700 }}>
                          {new Date(invoice?.invoice?.dueDate ?? '').toLocaleDateString('en-GB', { timeZone: 'UTC' })}
                        </Typography>
                      ),
                    },
                    {
                      label: 'Levy Month',
                      value: <Typography sx={{ fontWeight: 600 }}>{formatLevyMonth(invoice?.invoice?.dueDate)}</Typography>,
                    },
                  ]}
                  actions={
                    <>
                      <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => handleOpen(invoice)}>
                        View
                      </Button>
                      {isHoa && (
                        <Button variant="contained" startIcon={<EditIcon />} onClick={() => handleOpenEditInvoice(invoice)}>
                          Edit
                        </Button>
                      )}
                    </>
                  }
                />
              );
            })}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice Number</TableCell>
                  <TableCell>Invoice Amount</TableCell>
                  <TableCell>Invoice Balance</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInvoices.map((invoice: PropertyInvoiceWithRelations) => {
                  const isOverdue = new Date(invoice?.dueDate ?? '') < new Date() && invoice?.status !== 'Paid';

                  return (
                    <TableRow key={invoice.invoiceNumber}>
                      <TableCell>{invoice.invoiceNumber}</TableCell>
                      <TableCell>${invoice?.invoice?.totalCost.toFixed(2)}</TableCell>
                      <TableCell>${(invoice?.propertyInvoiceBalance ?? 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Typography
                          style={{
                            color: isOverdue ? 'lightcoral' : '#4CAF50',
                            fontWeight: 'bold'
                          }}>
                          {new Date(invoice?.invoice?.dueDate ?? '').toLocaleDateString('en-GB', { timeZone: 'UTC' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={renderInvoiceStatus(invoice?.status)} style={getInvoiceStatusStyles(invoice?.status)} />
                      </TableCell>

                      <TableCell align="right">
                        <IconButton color="primary" onClick={() => handleOpen(invoice)}>
                          <VisibilityIcon />
                        </IconButton>
                        {isHoa && (
                          <IconButton color="primary" onClick={() => handleOpenEditInvoice(invoice)}>
                            <EditIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {invoices.length > 0 && (
          <TablePagination
            component="div"
            count={invoices.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}

        <Modal open={open} onClose={handleClose}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: '70%', lg: '60%' },
            bgcolor: '#ffffff',
            boxShadow: 24,
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 3,
            overflowY: 'auto',
            maxHeight: '85vh',
            border: '1px solid #e5e7eb'
          }}>
            {selectedInvoice && (
              <>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CardMedia
                      component="img"
                      image={developmentInfo(development.name).logo || 'https://via.placeholder.com/120x40'}
                      alt="Development Logo"
                      sx={{ width: 140, height: 'auto', objectFit: 'contain' }}
                    />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f265c', letterSpacing: 0.5 }}>Invoice</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{development?.companyName}</Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={
                      selectedInvoice?.status === 'Paid'
                        ? 'Paid'
                        : selectedInvoice?.status === 'Unpaid'
                          ? 'Unpaid'
                          : selectedInvoice?.status === 'Cancelled'
                            ? 'Cancelled'
                            : 'Partially Paid'
                    }
                    sx={{
                      bgcolor:
                        selectedInvoice?.status === 'Paid'
                          ? '#16a34a'
                          : selectedInvoice?.status === 'Unpaid'
                            ? '#dc2626'
                            : selectedInvoice?.status === 'Cancelled'
                              ? '#6b7280'
                              : '#f59e0b',
                      color: '#fff',
                      fontWeight: 600
                    }}
                  />
                </Box>

                {/* Meta Bar */}
                <Box sx={{
                  background: 'linear-gradient(90deg, #1C4697 0%, #2f6cd1 100%)',
                  color: '#fff',
                  p: 2,
                  borderRadius: 2,
                  mb: 3,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                  gap: 2
                }}>
                  <Typography variant="body2" color="white"><strong>Levy Month:</strong> {formatLevyMonth(selectedInvoice?.invoice?.dueDate)}</Typography>
                  <Typography variant="body2" color="white"><strong>Invoice #:</strong> {selectedInvoice.invoiceNumber}</Typography>
                  <Typography variant="body2" color="white"><strong>Unit:</strong> {formatPropertyNumber(property)}</Typography>
                  <Typography variant="body2" color="white"><strong>Due Date:</strong> {new Date(selectedInvoice?.invoice?.dueDate ?? '').toLocaleDateString('en-GB', { timeZone: 'UTC' })}</Typography>
                </Box>

                {/* Parties */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary' }}>From</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{development.companyName}</Typography>
                    <Typography variant="body2">{development.address.street}</Typography>
                    <Typography variant="body2">{development.address.city}, {development.address.country}</Typography>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary' }}>Billed To</Typography>
                    {owners.length > 0 && owners[0] ? (
                      <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{owners[0].name}</Typography>
                        <Typography variant="body2">{owners[0].email}</Typography>
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Owner details unavailable</Typography>
                    )}
                    {owners.length > 1 && owners[1] && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{owners[1].name}</Typography>
                        <Typography variant="body2">{owners[1].email}</Typography>
                      </>
                    )}
                  </Paper>
                </Box>

                {/* Items Table */}
                <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: '#0f265c' }}>
                      <TableRow>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Description</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Quantity</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Unit Price</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }} align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedInvoice.invoice.items.map((item: any, index: number) => (
                        <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                          <TableCell>{item.description}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>${Number(item.unitPrice).toFixed(2)}</TableCell>
                          <TableCell align="right">${Number(item.totalCostPerItem).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Totals */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2, minWidth: 280, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Subtotal</Typography>
                      <Typography variant="body2">${Number(selectedInvoice.invoice.totalCost).toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>${Number(selectedInvoice.invoice.totalCost).toFixed(2)}</Typography>
                    </Box>
                  </Paper>
                </Box>

                {/* Banking Details */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#0f265c' }}>Banking Details</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Bank Name</Typography>
                      <Typography variant="body2">{development.bank.bank}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Branch Code</Typography>
                      <Typography variant="body2">{development.bank.bankCode}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Account Name</Typography>
                      <Typography variant="body2">{development.bank.accountName}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Account Number</Typography>
                      <Typography variant="body2">{development.bank.accountNumber}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Swift Code</Typography>
                      <Typography variant="body2">{development.bank.swiftCode}</Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 3 }}>
                  <Button variant="outlined" color="primary" onClick={() => window.print()}>Print</Button>
                  <Button variant="contained" color="primary" onClick={handleClose}>Close</Button>
                </Box>
              </>
            )}
          </Box>
        </Modal>

        {/* Edit Wallet Balance Dialog */}
        <Dialog open={editWalletOpen} onClose={handleCloseEditWallet} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Property Wallet Balance</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Wallet Balance"
              type="number"
              value={walletBalance}
              onChange={(e) => setWalletBalance(parseFloat(e.target.value) || 0)}
              inputProps={{ step: "0.01" }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditWallet}>Cancel</Button>
            <Button onClick={handleSaveWalletBalance} variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Invoice Dialog */}
        <Dialog open={editInvoiceOpen} onClose={handleCloseEditInvoice} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Invoice</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Invoice Balance"
                type="number"
                value={editInvoiceBalance}
                onChange={(e) => setEditInvoiceBalance(parseFloat(e.target.value) || 0)}
                inputProps={{ step: "0.01" }}
              />
              <Select
                fullWidth
                value={editInvoiceStatus}
                onChange={(e) => setEditInvoiceStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="Unpaid">Unpaid</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Partially Paid">Partially Paid</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditInvoice}>Cancel</Button>
            <Button onClick={handleSaveInvoice} variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box> :
      <LoadingCover />
  );
};

export default InvoicesList;
